import BackIcon from '../icons/back-icon';
import {
  NOTIFICATION_PREFERENCE_LAST_ROW_CLASS,
  NOTIFICATION_PREFERENCE_ROW_WITH_DIVIDER_CLASS,
} from './notification-styles';
import type { NotificationPreference } from './notification-types';
import { NotificationToggle } from './notification-toggle';

type NotificationPreferencesProps = {
  preferences: NotificationPreference[];
  values: Record<NotificationPreference['id'], boolean>;
  isLoading: boolean;
  error: string | null;
  updatingPreferenceId: string | null;
  isMobile: boolean;
  onBack: () => void;
  onRetry: () => void;
  onValueChange: (id: NotificationPreference['id'], value: boolean) => void;
};

export function NotificationPreferences({
  preferences,
  values,
  isLoading,
  error,
  updatingPreferenceId,
  isMobile,
  onBack,
  onRetry,
  onValueChange,
}: NotificationPreferencesProps) {
  return (
    <section
      aria-label={isMobile ? 'Cài đặt' : undefined}
      aria-labelledby={isMobile ? undefined : 'notification-preferences-title'}
    >
      {!isMobile ? (
        <div className="mb-5 flex items-center gap-3 px-2">
          <button
            type="button"
            onClick={onBack}
            aria-label="Quay lại thông báo"
            className="text-on-surface hover:bg-surface-variant focus-visible:ring-primary/50 flex size-8 shrink-0 items-center justify-center rounded-lg focus-visible:ring-2 focus-visible:outline-none"
          >
            <BackIcon size={24} aria-hidden />
          </button>
          <h2
            id="notification-preferences-title"
            className="font-display text-display-2xs leading-display-2xs xl:text-display-xs xl:leading-display-xs font-semibold"
          >
            Cài đặt thông báo
          </h2>
        </div>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="bg-error-container text-on-error-container mb-3 rounded-xl px-4 py-3 text-sm"
        >
          <p>{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="focus-visible:ring-primary/50 mt-2 font-semibold underline underline-offset-2 focus-visible:ring-2 focus-visible:outline-none"
          >
            Thử lại
          </button>
        </div>
      ) : null}
      {isLoading && preferences.length === 0 ? (
        <p className="text-on-surface-variant px-4 py-8 text-center text-sm">
          Đang tải cài đặt thông báo...
        </p>
      ) : null}
      <div aria-busy={isLoading || updatingPreferenceId !== null}>
        {preferences.map((preference, index) => (
          <div
            key={preference.id}
            className={
              index < preferences.length - 1
                ? NOTIFICATION_PREFERENCE_ROW_WITH_DIVIDER_CLASS
                : NOTIFICATION_PREFERENCE_LAST_ROW_CLASS
            }
          >
            <div className="min-w-0 flex-1">
              <h3 className="text-ui-base leading-sm font-semibold">
                {preference.label}
              </h3>
              <p className="text-on-surface-variant leading-xs mt-1 text-xs">
                {preference.description}
              </p>
            </div>
            <NotificationToggle
              checked={values[preference.id] ?? false}
              disabled={isLoading || updatingPreferenceId !== null}
              label={preference.label}
              onCheckedChange={checked => onValueChange(preference.id, checked)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
